import PencomDateField from "@/components/input/PencomDateField";
import PencomDropdown from "@/components/input/PencomDropdown";
import PencomTextField from "@/components/input/PencomTextField";
import type { FieldConfig } from "@/utils/organizationTypeFields";
import type { ValidateTinResult } from "@/models/responses/employer-registration.response";
import type { Dispatch, SetStateAction } from "react";

const TIN_STATUS_ELEMENT_ID = "tin-validation-status";

export type TinValidationUiState =
  | { status: "idle" }
  | { status: "validating" }
  | { status: "result"; data: ValidateTinResult };

export interface EmployerAdditionalFieldsSectionProps {
  fields: FieldConfig[];
  additionalFields: Record<string, string>;
  setAdditionalFields: Dispatch<SetStateAction<Record<string, string>>>;
  additionalFieldErrors: Record<string, string>;
  setAdditionalFieldErrors: Dispatch<SetStateAction<Record<string, string>>>;
  // TIN real-time validation (optional; only relevant when a tinNumber field
  // is present). State is owned by the page and rendered inline here.
  tinValidation?: TinValidationUiState;
  onTinBlur?: (value: string) => void;
  onTinChange?: () => void;
}

// Whether the current TIN result should block submission (independent of
// the message color below — NOT_FOUND is shown in red but doesn't block).
// Only a VALID result where BOTH the company name and RC number match what
// FIRS has on file is a clean pass. ERROR (FIRS outage), INVALID (FIRS
// rejected the TIN), and NOT_FOUND are all non-blocking by design — PenCom
// verifies/re-checks the TIN during admin review either way.
function isBlockingTinState(state: TinValidationUiState): boolean {
  if (state.status !== "result") return false;
  const { result, crossCheck } = state.data;
  return (
    result.status === "NOT_ACTIVATED" ||
    (result.status === "VALID" &&
      (!crossCheck.rcMatches || !crossCheck.nameMatches))
  );
}

const TinValidationStatus = ({
  state,
}: {
  state: TinValidationUiState;
}) => {
  const baseClass =
    "text-sm mt-2 font-clash-display font-regular flex items-start gap-x-1.5";

  let content: React.ReactNode = null;

  if (state.status === "validating") {
    content = <span className="text-medium-grey">Validating…</span>;
  } else if (state.status === "result") {
    const { result, crossCheck } = state.data;

    if (result.status === "VALID" && crossCheck.rcMatches && crossCheck.nameMatches) {
      content = (
        <span className="text-green-600">
          <span aria-hidden="true">✓ </span>
          Verified: {result.taxPayerName}
        </span>
      );
    } else if (
      result.status === "VALID" &&
      (!crossCheck.rcMatches || !crossCheck.nameMatches)
    ) {
      content = (
        <span className="text-red-500">
          This TIN does not match FIRS records. Please check it and enter the
          correct TIN. You can also enter the company name and RC number
          exactly as they appear on your CAC certificate by clicking the Back
          button at the top.
        </span>
      );
    } else if (result.status === "NOT_FOUND") {
      content = (
        <span className="text-red-500">
          TIN not found - We couldn't verify this TIN with FIRS. Please
          double-check the number or contact FIRS. You can continue with your
          application. Your TIN will be manually reviewed.
        </span>
      );
    } else if (result.status === "NOT_ACTIVATED") {
      content = (
        <span className="text-red-500">
          This Tax ID isn't active yet. Please activate it on Rev360, then try
          again.
        </span>
      );
    } else if (result.status === "INVALID") {
      content = (
        <span className="text-medium-grey">
          This Tax ID (TIN) is invalid. You can continue with registration —
          PenCom will manually review it.
        </span>
      );
    } else {
      // ERROR — FIRS outage/timeout/429/500. Neutral, non-blocking. No retry
      // action is offered here — an outage isn't something re-clicking fixes.
      content = (
        <span className="text-medium-grey">
          We couldn't reach FIRS to verify your TIN right now. You can continue
          — PenCom will verify it during review.
        </span>
      );
    }
  }

  return (
    <div
      id={TIN_STATUS_ELEMENT_ID}
      role="status"
      aria-live="polite"
      className={baseClass}
    >
      {content}
    </div>
  );
};

export const EmployerAdditionalFieldsSection = ({
  fields,
  additionalFields,
  setAdditionalFields,
  additionalFieldErrors,
  setAdditionalFieldErrors,
  tinValidation = { status: "idle" },
  onTinBlur,
  onTinChange,
}: EmployerAdditionalFieldsSectionProps) => {
  const tinInvalid = isBlockingTinState(tinValidation);

  return (
    <>
      {fields.map((fieldConfig) => {
        const isTin = fieldConfig.name === "tinNumber";

        return (
          <div key={fieldConfig.name}>
            {fieldConfig.type === "select" ? (
              <PencomDropdown
                id={fieldConfig.name}
                label={fieldConfig.label}
                placeholder={fieldConfig.placeholder}
                options={
                  fieldConfig.options?.map((option) => ({
                    id: option.value,
                    label: option.label,
                  })) || []
                }
                value={additionalFields[fieldConfig.name] || ""}
                onChange={(e) => {
                  setAdditionalFields((prev) => ({
                    ...prev,
                    [fieldConfig.name]: e.target.value,
                  }));
                  setAdditionalFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next[fieldConfig.name];
                    return next;
                  });
                }}
                bg="white"
                helperText={
                  fieldConfig.required && additionalFieldErrors[fieldConfig.name]
                    ? additionalFieldErrors[fieldConfig.name]
                    : undefined
                }
              />
            ) : fieldConfig.type === "date" ? (
              <PencomDateField
                id={fieldConfig.name}
                label={fieldConfig.label}
                placeholder={fieldConfig.placeholder}
                value={additionalFields[fieldConfig.name] || ""}
                onChange={(value) => {
                  setAdditionalFields((prev) => ({
                    ...prev,
                    [fieldConfig.name]: value,
                  }));
                  setAdditionalFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next[fieldConfig.name];
                    return next;
                  });
                }}
                error={!!additionalFieldErrors[fieldConfig.name]}
                helperText={additionalFieldErrors[fieldConfig.name]}
              />
            ) : (
              <PencomTextField
                id={fieldConfig.name}
                label={fieldConfig.label}
                placeholder={
                  fieldConfig.placeholder ||
                  `Enter ${fieldConfig.label.toLowerCase()}`
                }
                type="text"
                value={additionalFields[fieldConfig.name] || ""}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  const nextValue = isTin ? rawValue.slice(0, 13) : rawValue;

                  setAdditionalFields((prev) => ({
                    ...prev,
                    [fieldConfig.name]: nextValue,
                  }));
                  setAdditionalFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next[fieldConfig.name];
                    return next;
                  });
                  if (isTin) onTinChange?.();
                }}
                onBlur={
                  isTin
                    ? (e) => onTinBlur?.(e.target.value)
                    : undefined
                }
                aria-describedby={isTin ? TIN_STATUS_ELEMENT_ID : undefined}
                aria-invalid={isTin ? tinInvalid : undefined}
                multiline={fieldConfig.multiline}
                rows={fieldConfig.multiline ? 4 : undefined}
                onTextareaChange={
                  fieldConfig.multiline
                    ? (e) => {
                        setAdditionalFields((prev) => ({
                          ...prev,
                          [fieldConfig.name]: e.target.value,
                        }));
                        setAdditionalFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next[fieldConfig.name];
                          return next;
                        });
                      }
                    : undefined
                }
                bg="white"
                error={!!additionalFieldErrors[fieldConfig.name]}
                helperText={additionalFieldErrors[fieldConfig.name] || ""}
              />
            )}

            {isTin && <TinValidationStatus state={tinValidation} />}
          </div>
        );
      })}
    </>
  );
};
